<script lang="ts">
  import { userStore } from "$lib/stores/user";
  import UserAddIcon from "$assets/icons/user-add.svelte";
  import AppsIcon from "$assets/icons/apps.svelte";

  import Button from "$lib/ui/button/button.svelte";
  import LogoGraphic from "$assets/icons/logo-graphic.svelte";
  import ExternalLinkIcon from "$assets/icons/external-link.svelte";
  import BlogIcon from "$assets/icons/blog.svelte";
  import { selectedTeamStore } from "$lib/stores/teams";
  import { environment } from "$lib/utils/env";
  import TeamNotFound from "./TeamNotFound.svelte";
  import SectionTitle from "$lib/components/shared/SectionTitle.svelte";
  import SmallCard from "$lib/components/shared/SmallCard.svelte";
  import MediumCard from "$lib/components/shared/MediumCard.svelte";

  $: team = $selectedTeamStore;
  $: user = $userStore;

  let docsLink = "";
  environment.then((env) => {
    docsLink = `${env.apiRoots.management}/docs`;
  });
</script>

<SectionTitle>Overview</SectionTitle>
{#if team}
  <div class="flex-1 flex flex-col gap-16">
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
      <SmallCard
        title="Apps"
        href={`/t/${team.id}/apps`}
        label={team?.apps.length <= 0 ? "Create First App" : "View Apps"}
      >
        {team?.apps.length}
        <AppsIcon slot="icon" />
      </SmallCard>
      <SmallCard title="Members" href={`/t/${team.id}/members`} label="View Members">
        {team?.members.length}
        <UserAddIcon slot="icon" />
      </SmallCard>
    </div>
    <div class="gap-8 xl:gap-16 grid grid-cols-1 md:grid-cols-3">
      <div class="md:col-span-2 flex flex-col gap-4">
        <div class="font-semibold text-lg border-b border-general pb-4 mb-4 flex justify-between items-center">
          <h3>Some helpful guides</h3>
          <Button
            variant="link"
            href="https://www.redactive.ai/blog"
            class="flex items-center gap-2 [&>svg]:w-4"
            target="_blank"
            rel="noreferrer"
          >
            View Blog
            <ExternalLinkIcon />
          </Button>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 h-full">
          <MediumCard
            title="You don't want a Vector Database"
            href="https://www.redactive.ai/blog/you-dont-want-a-vector-database"
          >
            <BlogIcon slot="icon" />
            <div slot="description">
              All of this interest in Vector Databases is driven by the explosion of Generative AI that ChatGPT sparked
              (less than 18 months ago!). Specifically, that Vector Databases enable the Retrieval Augmented Generation
              (RAG) paradigm, which is currently the leading form of prompt engineering and actually extracting business
              value from Large Language Models (LLMs).
            </div>
            <span slot="link">
              <Button class="self-start flex items-center gap-2 [&>svg]:w-4" variant="link" size="lg">
                Read more
                <ExternalLinkIcon />
              </Button>
            </span>
          </MediumCard>
          <MediumCard
            title="Why hasn't AI taken over the Finance Industry?"
            href="https://www.redactive.ai/blog/why-hasnt-ai-taken-over-the-finance-industry"
          >
            <BlogIcon slot="icon" />
            <div slot="description">
              AI has already proven to be a revolutionary force around the world. From deciphering dead languages to
              finding previously unknown patterns in historical data, it is redefining our understanding of our past.
              With content creation and recommendation systems on platforms we use daily it changes the way we view our
              present. And with assisting on health diagnosis and the creation of new pharmaceutical drugs it is already
              changing our shared future. Silicon Valley is also betting big on AI as the most important tech
              breakthrough since the smartphone. For all of this, AI has yet to be a revolutionary force in the
              financial services industry (FSI). Despite the huge amount of blogs, articles, and papers dedicated to the
              subject, as someone who’s job it is to watch for fintech trends around the world I have been extremely
              underwhelmed by the so called “impact” of AI in financial services. Unlike the humanities, entertainment,
              and healthcare industries listed above, when you read any article on the impact of AI in finance it
              usually comes down to a few key areas: credit decisioning, fraud and AML, and (sigh) chatbots. The lack of
              imagination is not the author’s faults - there are simply very few real world examples thus far of AI
              working in the financial industry. The better question then “what is possible” for AI in finance is “why
              isn’t finance adopting AI more quickly?”.
            </div>
            <span slot="link">
              <Button class="self-start flex items-center gap-2 [&>svg]:w-4" variant="link" size="lg">
                Read more
                <ExternalLinkIcon />
              </Button>
            </span>
          </MediumCard>
        </div>
      </div>
      <div class="flex-1 flex flex-col gap-4">
        <div class="font-semibold text-lg border-b border-general pb-4 mb-4">Helpful docs</div>
        <div class="h-full">
          <MediumCard
            title="Learn about Permissions Assurance"
            href="https://www.redactive.ai/blog/why-hasnt-ai-taken-over-the-finance-industry"
          >
            <LogoGraphic slot="icon" />
            <div slot="description">
              Get detailed information on how Permissions Assurance ensures seamless integration and maintains high security
              standards.
            </div>
            <span slot="link">
              <Button class="self-start flex items-center gap-2 [&>svg]:w-4" variant="link" size="lg">
                View Docs
                <ExternalLinkIcon />
              </Button>
            </span>
          </MediumCard>
        </div>
      </div>
    </div>
  </div>
{:else}
  <TeamNotFound />
{/if}
